import { Injectable, OnDestroy } from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { filter, Subscription } from 'rxjs'
import { CacheService } from './cache.service'

const NOT_SIGNED_IN_MSG = 'Cannot start site log poll without being signed in.'
const WAIT_FOR_USER_MS = 250
const WAIT_FOR_USER_ATTEMPTS = 20

@Injectable({
  providedIn: 'root',
})
export class SiteLogPollRouteService implements OnDestroy {
  private routerSubscription?: Subscription
  private currentSiteId: string | null = null

  constructor(
    private router: Router,
    private cache: CacheService
  ) {
    this.handleRouteChange()

    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.handleRouteChange())
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe()
    if (this.currentSiteId) {
      this.cache.stopSiteLogPoll()
      this.currentSiteId = null
    }
  }

  private handleRouteChange(): void {
    const url = this.router.url.split('?')[0]

    const siteLogMatch = url.match(
      /\/country\/[^/]+\/presence\/[^/]+\/site\/([^/]+)\/log$/
    )
    const locationLogMatch = url.match(
      /\/country\/[^/]+\/presence\/[^/]+\/site\/([^/]+)\/location\/[^/]+\/log$/
    )
    const columnLogMatch = url.match(
      /\/country\/[^/]+\/presence\/[^/]+\/site\/([^/]+)\/location\/[^/]+\/column\/[^/]+\/log$/
    )
    const lockerSessionDetailsMatch = url.match(
      /\/country\/[^/]+\/presence\/[^/]+\/site\/([^/]+)\/location\/[^/]+\/column\/[^/]+\/locker\/[^/]+\/session-details$/
    )

    const match =
      siteLogMatch ||
      locationLogMatch ||
      columnLogMatch ||
      lockerSessionDetailsMatch
    const siteId = match ? match[1] : null

    if (siteId && siteId !== this.currentSiteId) {
      if (this.currentSiteId) {
        this.cache.stopSiteLogPoll()
      }
      this.currentSiteId = siteId
      this.tryStartSiteLogPoll(siteId)
    } else if (!siteId) {
      // Every other page: no site log poll, whoever started it.
      this.cache.stopSiteLogPoll()
      this.currentSiteId = null
    }
  }

  private tryStartSiteLogPoll(siteId: string): void {
    const start = async () => {
      await this.cache.isReady()
      for (let i = 0; i < WAIT_FOR_USER_ATTEMPTS; i++) {
        const user = await this.cache.getActiveUser()
        if (user && this.currentSiteId === siteId) {
          try {
            await this.cache.startSiteLogPoll(siteId)
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err)
            if (msg !== NOT_SIGNED_IN_MSG) {
              console.error('Failed to start site log poll:', err)
            }
          }
          return
        }
        if (this.currentSiteId !== siteId) return
        await new Promise(r => setTimeout(r, WAIT_FOR_USER_MS))
      }
    }
    void start()
  }
}
